import { useCallback } from "react";

import {
  buildAmbientScribeNoteQuery,
  useGetAmbientScribeNotesQuery
} from "services/ambient-scribe";
import {
  AMBIENT_SCRIBE_NOTE_STATUS,
  type AmbientScribeNote,
  type AmbientScribeNoteFilters,
  type AmbientScribeNoteParentType
} from "services/types/ambient-scribe-note.type";

/**
 * The newest generated note of one kind for one recording, optionally narrowed to one artifact.
 *
 * The backend sets `parent_id` on failed runs as well as successful ones, so a single
 * `?ambient_scribe=&parent_type=&limit=1&ordering=-pk` read is authoritative: it is the latest
 * attempt, whatever its outcome. Requesting one row matters because the projection carries
 * `prompt`, which embeds the whole transcript.
 */

type UseLatestScribeNoteArgs = {
  /** `AmbientScribe.id` is `number | string`; normalized below. */
  scribeId: number | string | undefined;
  parentType: AmbientScribeNoteParentType;
  /**
   * Narrow to one artifact, or omit for every note of this `parentType` on this scribe.
   *
   * **Omitting it is right for SOAP and wrong for audiology.** A SOAP run is stamped with
   * `parent_id = scribe.chart_note_id` — the recording's OWN linked note, `NULL` when it has none —
   * not the note whichever screen opened. The Insert Scribe dialog lets a user pick any recording,
   * so scoping SOAP by the caller's chart note id matches almost nothing. And it does not need to:
   * the SOAP output lives on `scribe.soap_notes`, which any run rewrites, so the newest ChartNote
   * row IS the run behind what is on screen.
   *
   * An audiology summary is the opposite — one scribe can carry summaries for several reports and
   * the note itself is the output, so that caller must always pass an id.
   * `useAudiologyReportSummaryNote` enforces it.
   */
  parentId?: number;
  enabled: boolean;
  /** Adds `prompt` to the projection. Only for surfaces that actually display it. */
  withPrompt?: boolean;
  /**
   * Ask for `sent_email_id` (whether this note was emailed, and in which email).
   *
   * Opt-in because it rides a separate RestQL projection: the shared one also serves the
   * audiology tab, which must not start naming a field an older backend lacks.
   */
  withSentEmail?: boolean;
};

type UseLatestScribeNoteResult = {
  /** This artifact's newest attempt, succeeded or failed. Drives error display. */
  newest: AmbientScribeNote | undefined;
  /**
   * Newest attempt that produced content. Usually the same row as `newest`; falls back to the
   * previous successful run when the newest attempt failed.
   */
  latestSucceeded: AmbientScribeNote | undefined;
  /** The prompt behind `latestSucceeded`, or behind a failure when that is all there is. */
  prompt: string | undefined;
  isLoading: boolean;
  /**
   * True while a read is in flight, INCLUDING a refetch over data that is already on screen —
   * which `isLoading` (first-load only) does not report.
   *
   * Gate any action that consumes the note on this after a write. A PATCH invalidates
   * `AmbientScribeNotes` and returns before the refetch lands, so an Email or Print fired in that
   * window would use the value this hook is still holding — the PRE-edit letter. Same shape as the
   * root CLAUDE.md's "`skip` does not clear `data`" rule: the previous response stays readable
   * until the new one arrives.
   */
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
};

const hasContent = (note: AmbientScribeNote | undefined) =>
  note?.status === AMBIENT_SCRIBE_NOTE_STATUS.SUCCEEDED && Boolean(note.notes);

export default function useLatestScribeNote({
  scribeId,
  parentType,
  parentId,
  enabled,
  withPrompt = false,
  withSentEmail = false
}: UseLatestScribeNoteArgs): UseLatestScribeNoteResult {
  // Callers read the scribe id from different places (a selected list row vs. the detail query),
  // so without this `"180"` and `180` become separate RTK cache entries: two requests, and a
  // screen that can disagree with itself.
  const numericScribeId = scribeId != null ? Number(scribeId) : Number.NaN;
  const hasIds = Number.isFinite(numericScribeId);

  // Derived once and reused by both subscriptions AND by `refetchAll` below. Keeping them off one
  // expression is the point: when the refetch guard and the `skip` it guards drift apart, the
  // symptom is an RTK Query throw that takes the whole route down (SN-19067).
  const isSkipped = !enabled || !hasIds;

  const baseFilters: AmbientScribeNoteFilters = {
    ambient_scribe: numericScribeId,
    parent_type: parentType,
    // Spread rather than `parent_id: parentId` so an unscoped read omits the key entirely. RTK
    // Query keys its cache on the args object, and `{parent_id: undefined}` is a different key from
    // `{}` — leaving it in would split scoped and unscoped callers into two cache entries that
    // issue the identical request.
    ...(parentId != null ? { parent_id: parentId } : {}),
    limit: 1,
    ordering: "-pk",
    query: buildAmbientScribeNoteQuery({ withPrompt, withSentEmail })
  };

  const {
    data: newestData,
    isLoading,
    isFetching,
    isError,
    refetch
  } = useGetAmbientScribeNotesQuery(baseFilters, {
    skip: isSkipped,
    // The note is generated out of band, so a list cached from a previous open is stale by
    // definition.
    refetchOnMountOrArgChange: true
  });

  const newest = newestData?.results?.[0];
  const newestFailed = newest?.status === AMBIENT_SCRIBE_NOTE_STATUS.FAILED;

  // A failed retry must not look like it destroyed a good summary, so when the newest attempt
  // failed we go back for the last one that produced content. Skipped entirely otherwise, which
  // is every happy-path open — this costs zero extra requests until something actually fails.
  const {
    data: succeededData,
    isFetching: isFetchingSucceeded,
    refetch: refetchSucceeded
  } = useGetAmbientScribeNotesQuery(
    { ...baseFilters, status: AMBIENT_SCRIBE_NOTE_STATUS.SUCCEEDED },
    {
      skip: isSkipped || !newestFailed,
      refetchOnMountOrArgChange: true
    }
  );

  const latestSucceeded = hasContent(newest)
    ? newest
    : (() => {
        const candidate = succeededData?.results?.[0];
        return hasContent(candidate) ? candidate : undefined;
      })();

  // Stable identity: both consumers put this in a `useEffect` dep array to refetch when a
  // generation finishes, and a fresh function each render would re-run those effects constantly.
  //
  // The `isSkipped` guard is load-bearing, not defensive. RTK Query throws
  // "Cannot refetch a query that has not been started yet." from `refetch()` on a subscription
  // that `skip` never started — and both consumers fire this from an effect keyed on a GENERATION
  // flag, an axis independent of `enabled`. With `ambientScribeTemplateTypes` off, the SOAP caller
  // is skipped while its effect still sees `is_generating_soap` go true → false, which took the
  // whole `/medical-scribes` route into the error boundary on every Apply. The audiology tab has
  // the same shape via an undefined `reportId`. A skipped read has nothing to refetch, so this is
  // a no-op here rather than a precondition every call site has to restate.
  const refetchAll = useCallback(() => {
    if (isSkipped) return;
    refetch();
    // Past the guard above, the fallback's own skip is exactly `!newestFailed`.
    if (newestFailed) refetchSucceeded();
  }, [isSkipped, refetch, refetchSucceeded, newestFailed]);

  return {
    newest,
    latestSucceeded,
    // Prefer the prompt behind the content on screen; fall back to the failed run's prompt,
    // which is the only one there is when nothing ever succeeded — and the most useful one to
    // read when diagnosing why.
    prompt: latestSucceeded?.prompt ?? newest?.prompt ?? undefined,
    isLoading,
    // Either read can be the one holding a stale letter: when the newest attempt failed, the
    // displayed content comes from the fallback query.
    isFetching: isFetching || isFetchingSucceeded,
    isError,
    refetch: refetchAll
  };
}
